import { useState, useEffect, useMemo } from 'react'
import { fetchOptions } from 'src/mentor/services/emrOptionsApi'

/**
 * Custom hook for searchable multi-select with pills UI
 * Handles search state and debounced search (searches even with empty string)
 *
 * @param {Object} field - The field definition containing optionSource
 * @param {string} defaultQueryName - Default query name if not specified in field
 * @param {Array} selectedItems - Currently selected items (valueStringList)
 * @param {number} pageSize - Number of options to fetch (default: 15)
 * @param {boolean} hideSelected - Whether to hide selected items from results (default: false)
 * @param {boolean} hideStatic - Whether to hide static options from results (default: true)
 * @returns {Object} Search state and results
 */
export function useSearchableOptions(
  field,
  defaultQueryName,
  selectedItems = [],
  pageSize = 15,
  hideSelected = false,
  hideStatic = true,
) {
  const [searchText, setSearchText] = useState('')
  const [rawResults, setRawResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)

  // Memoize selected codes set for filtering
  const selectedCodes = useMemo(() => new Set(selectedItems.map((item) => item.code)), [selectedItems])

  // Memoize static codes set for filtering
  const staticCodes = useMemo(
    () => new Set(field?.optionSource?.staticOptions?.map((opt) => opt.code) || []),
    [field?.optionSource?.staticOptions],
  )

  // Debounced search for options (runs even with empty string to show initial results)
  // Only triggers on searchText change, NOT on selection change
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!field?.optionSource?.queryName && !defaultQueryName) {
        return
      }

      setIsSearching(true)
      try {
        const options = await fetchOptions(
          field?.optionSource?.queryName || defaultQueryName,
          searchText,
          {},
          pageSize,
        )

        setRawResults(options)
      } catch (error) {
        console.error('[useSearchableOptions] Search error:', error)
        setRawResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchText, field?.optionSource?.queryName, defaultQueryName, pageSize])

  // Filter results client-side based on configuration
  const searchResults = useMemo(() => {
    return rawResults.filter((opt) => {
      // Filter out static options if hideStatic is true
      if (hideStatic && staticCodes.has(opt.value)) {
        return false
      }

      // Filter out selected items if hideSelected is true
      if (hideSelected && selectedCodes.has(opt.value)) {
        return false
      }

      return true
    })
  }, [rawResults, staticCodes, selectedCodes, hideStatic, hideSelected])

  return {
    searchText,
    setSearchText,
    searchResults,
    isSearching,
  }
}
